import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, Grid, Button, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import { useCreateCategory } from "../../hooks/categories/useCreateCategory";
import { useUpdateCategory } from "../../hooks/categories/useUpdateCategory";
// import { useGetCategoriesForSelect } from "../../hooks/categories/useGetCategoriesForSelect";
// import { useGetDepartments } from "../../hooks/Departments/useGetDepartments";
import FormInput from "../inputs/FormInput";
import LanguageToggle from "../global/LanguageToggle";
import { slugify } from "../../utils/slugify";
// import { buildHierarchicalOptions } from "../../utils/helpers";
import toast from "react-hot-toast";

const FormAddEditCategories = ({ currentData, isEdit = false, onSubmitSuccess }) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng.startsWith("ar");
    const initialLang = localStorage.getItem("i18nextLng") || i18next.language || "ar";

    const [activeLang, setActiveLang] = useState(initialLang.startsWith("ar") ? "ar" : "en");
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    // Form States
    const [nameAr, setNameAr] = useState("");
    const [nameEn, setNameEn] = useState("");
    const [slug, setSlug] = useState("");
    // const [departmentId, setDepartmentId] = useState("");
    // const [parentId, setParentId] = useState("");
    // const [descriptionAr, setDescriptionAr] = useState("");
    // const [descriptionEn, setDescriptionEn] = useState("");
    // const [sortOrder, setSortOrder] = useState(0);
    const [isActive, setIsActive] = useState(true);
    const [image, setImage] = useState(null); // Can be: image object {id, url}, temp filename (string), or null

    // Hooks
    const { createCategoryMutate, isPending: isCreating } = useCreateCategory();
    const { editCategory, isUpdate } = useUpdateCategory();
    // const { departments, isLoading: loadingDepartments } = useGetDepartments({ per_page: 100 });
    // const { options: categoryOptions, isLoading: loadingCategories } = useGetCategoriesForSelect({
    //     department_id: departmentId || undefined
    // });

    const loadingSubmit = isCreating || isUpdate;


    // Auto-slug generation from English name (only in creation or if slug is empty)
    useEffect(() => {
        if (activeLang === "en" && nameEn && (!isEdit || !slug)) {
            setSlug(slugify(nameEn));
        }
    }, [nameEn, activeLang, isEdit, slug]);

    // Fill form data in edit mode
    useEffect(() => {
        if (isEdit && currentData) {
            setNameAr(currentData?.name?.ar || "");
            setNameEn(currentData?.name?.en || "");
            setSlug(currentData.slug || "");
            // setDepartmentId(currentData.department_id || "");
            // setParentId(currentData.parent_id || "");
            // setDescriptionAr(currentData.description?.ar || "");
            // setDescriptionEn(currentData.description?.en || "");
            setIsActive(currentData.is_active ?? true);
            setImage(currentData.image || currentData?.image_url || null);
        }
    }, [isEdit, currentData]);

    const handleSlugChange = (e) => {
        // Force English/Slug format: lowercase, numbers, hyphens only
        const val = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "");
        setSlug(val);
    };

    const handleImageChange = (e) => {
        const val = e.target.value;
        setImage(val);
    };

    // Hierarchy processing for parent category selection
    // const filteredCategoryOptions = useMemo(() => {
    //     return buildHierarchicalOptions(categoryOptions, currentData?.id);
    // }, [categoryOptions, currentData]);

    const SubmitForm = (e) => {
        e.preventDefault();

        // Validate slug is present and English
        if (!slug) {
            toast.error(isArabic ? "يرجى إدخال الرابط (Slug)" : "Please enter a slug");
            return;
        }

        // if (!departmentId) {
        //     const errorMsg = isArabic
        //         ? "يرجى اختيار القطاع لإتمام العملية"
        //         : "Please select a department to proceed";
        //     toast.error(errorMsg);
        //     return;
        // }

        const payload = {
            name: { ar: nameAr || nameEn, en: nameEn || nameAr },
            slug: slug,
            // department_id: departmentId || null,
            // parent_id: parentId || null,
            // description: { ar: descriptionAr || descriptionEn, en: descriptionEn || descriptionAr },
            sort_order: isEdit ? currentData.sort_order : null,
            is_active: isActive,
            image: image,
        };

        if (isEdit && currentData?.id) {
            editCategory(
                { id: currentData.id, categoryData: payload },
                { onSuccess: () => { if (onSubmitSuccess) onSubmitSuccess(); } }
            );
        } else {
            createCategoryMutate(payload, {
                onSuccess: () => { if (onSubmitSuccess) onSubmitSuccess(); }
            });
        }
    };

    return (
        <Paper
            sx={{
                p: 3,
                mt: 2,
                direction: isArabic ? "rtl" : "ltr",
                backgroundColor: isDark ? "transparent" : "#FFFFFF",
            }}
        >
            <Box component="form" onSubmit={SubmitForm} sx={{ p: { xs: 2, md: 4 }, display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 2 }}>
                    <Typography variant="h6">
                        {isEdit ? (isArabic ? "تعديل فئة" : "Edit Category") : (isArabic ? "إضافة فئة جديدة" : "Create New Category")}
                    </Typography>

                    <LanguageToggle activeLang={activeLang} onChange={(lng) => setActiveLang(lng)} showLabel={true} />
                </Box>

                <Grid container spacing={3}>
                    {/* Image Upload */}
                    <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'column', alignItems: 'center', gap: 2, }}>
                        <FormInput
                            type="file"
                            name="Image"
                            labelImage={isArabic ? "شعار الفئة" : "Category Image"}
                            value={image}
                            onChange={handleImageChange}
                            width={120}
                            height={120}
                            required={!isEdit && !image}
                        />
                    </Grid>
                    {/* Multilingual Names */}
                    {activeLang === "ar" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="الاسم (عربي)"
                                placeholder="أدخل اسم الفئة بالعربي"
                                type="text"
                                value={nameAr}
                                onChange={(e) => setNameAr(e.target.value)}
                                required
                            />
                        </Grid>
                    )}

                    {activeLang === "en" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="Name (English)"
                                placeholder="Enter category name in English"
                                type="text"
                                value={nameEn}
                                onChange={(e) => setNameEn(e.target.value)}
                                required
                            />
                        </Grid>
                    )}

                    {/* Slug (Auto-generated in EN, Manual in AR) */}
                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "الرابط (Slug - إنجليزي فقط)" : "Slug (English only)"}
                            placeholder="electronics-gadgets"
                            type="text"
                            value={slug}
                            onChange={handleSlugChange}
                            disabled={activeLang === "en"} // Auto-generated in English mode
                            required
                            helperText={activeLang === "ar" ? (isArabic ? "يجب إدخال الرابط باللغة الإنجليزية" : "Slug must be entered in English") : ""}
                        />
                    </Grid>

                    {/* Department Select */}
                    {/* <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "القطاع" : "Department"}
                            type="select"
                            name="department_id"
                            value={departmentId}
                            options={departments}
                            onChange={(e) => {
                                setDepartmentId(e.target.value);
                                setParentId(""); // Clear parent when department changes
                            }}
                            disabled={loadingDepartments}
                            required={true} // This adds visual asterisk but doesn't stop custom submission logic sometimes
                        />
                    </Grid> */}

                    {/* Parent Category Select */}
                    {/* <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "الفئة الأب (اختياري)" : "Parent Category (Optional)"}
                            type="select"
                            name="parent_id"
                            value={parentId}
                            options={filteredCategoryOptions}
                            onChange={(e) => setParentId(e.target.value)}
                            disabled={loadingCategories}
                            required={false}
                        />
                    </Grid> */}

                    {/* Multilingual Descriptions */}
                    {/* {activeLang === "ar" && (
                        <Grid item xs={12}>
                            <FormInput
                                label="الوصف (عربي)"
                                placeholder="أدخل وصف الفئة بالعربي"
                                type="textarea"
                                value={descriptionAr}
                                onChange={(e) => setDescriptionAr(e.target.value)}
                                minRows={3}
                            />
                        </Grid>
                    )}

                    {activeLang === "en" && (
                        <Grid item xs={12}>
                            <FormInput
                                label="Description (English)"
                                placeholder="Enter category description in English"
                                type="textarea"
                                value={descriptionEn}
                                onChange={(e) => setDescriptionEn(e.target.value)}
                                minRows={3}
                            />
                        </Grid>
                    )} */}

                    {/* Is Active Switch */}
                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "حالة الفئة (نشط)" : "Category Status (Active)"}
                            type="switch"
                            name="is_active"
                            checked={isActive}
                            onChange={(e) => setIsActive(e.target.checked)}
                        />
                    </Grid>
                </Grid>

                <Box mt={4} display="flex" justifyContent="flex-end" gap={2}>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={loadingSubmit}
                        sx={{ minWidth: 120 }}
                    >
                        {loadingSubmit ? (isArabic ? "جارٍ الحفظ..." : "Saving...") : (isEdit ? (isArabic ? "تحديث" : "Update") : (isArabic ? "حفظ" : "Save"))}
                    </Button>
                </Box>
            </Box>
        </Paper>
    );
};

export default FormAddEditCategories;